<script setup lang="ts">
const props = defineProps<{
  privacy1?: boolean,
  privacy1b?: boolean,
  privacy2?: boolean,
  privacy3?: boolean,
  privacy4?: boolean,
  hasError?: boolean,
  error?: string,
  hasError1b?: boolean,
  error1b?: string,
}>()

const emit = defineEmits(['change:privacy1', 'change:privacy1b', 'change:privacy2', 'change:privacy3', 'change:privacy4'])

const detailsOpen = ref(false)

const togglePrivacy1 = (e: Event) => emit('change:privacy1', (e.target as HTMLInputElement).checked)
const togglePrivacy1b = (e: Event) => emit('change:privacy1b', (e.target as HTMLInputElement).checked)
const togglePrivacy2 = (e: Event) => emit('change:privacy2', (e.target as HTMLInputElement).checked)
const togglePrivacy3 = (e: Event) => emit('change:privacy3', (e.target as HTMLInputElement).checked)
const togglePrivacy4 = (e: Event) => emit('change:privacy4', (e.target as HTMLInputElement).checked)
</script>

<template>
  <div class="w-full flex flex-col items-start justify-center gap-4">

    <p class="font-bold text-slate-700">Condizioni e Privacy</p>

    <!-- Consenso 1: Condizioni Generali (obbligatorio) -->
    <div class="flex flex-col gap-1">
      <div class="flex justify-start items-start gap-2">
        <!-- Input nativo sr-only per accessibilità tastiera/screen reader (WCAG 4.1.2) -->
        <input
          type="checkbox"
          id="privacy1-checkbox"
          :checked="privacy1"
          class="sr-only"
          :aria-required="true"
          :aria-invalid="hasError ? 'true' : 'false'"
          :aria-describedby="hasError ? 'privacy1-error' : undefined"
          @change="togglePrivacy1"
        />
        <label for="privacy1-checkbox" class="flex items-start gap-2 cursor-pointer select-none">
          <div class="w-5 h-5 min-w-5 min-h-5 shrink-0 mt-0.5 border-2 rounded-sm flex items-center justify-center transition-colors duration-150"
               aria-hidden="true"
               :style="privacy1 ? 'background-color:#6A40D8; border-color:#6A40D8' : 'background-color:white; border-color:#94a3b8'">
            <svg v-if="privacy1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" class="w-3 h-3" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
            </svg>
          </div>
          <span class="text-xs text-slate-600 text-start">
            Accetto le <NuxtLink class="text-primary hover:text-primary/80 font-bold" to="/termini-e-condizioni" target="_blank">Condizioni Generali di Utilizzo</NuxtLink> per il servizio di comparazione e di gestione pratica. <strong>(Obbligatorio)</strong>
          </span>
        </label>
      </div>
      <span v-if="hasError" id="privacy1-error" role="alert" class="text-xs text-red-600 font-bold text-start ml-7">{{ error }}</span>
    </div>

    <!-- Consenso 2: Informativa Privacy (obbligatorio) -->
    <div class="flex flex-col gap-1">
      <div class="flex justify-start items-start gap-2">
        <input
          type="checkbox"
          id="privacy1b-checkbox"
          :checked="privacy1b"
          class="sr-only"
          :aria-required="true"
          :aria-invalid="hasError1b ? 'true' : 'false'"
          :aria-describedby="hasError1b ? 'privacy1b-error' : undefined"
          @change="togglePrivacy1b"
        />
        <label for="privacy1b-checkbox" class="flex items-start gap-2 cursor-pointer select-none">
          <div class="w-5 h-5 min-w-5 min-h-5 shrink-0 mt-0.5 border-2 rounded-sm flex items-center justify-center transition-colors duration-150"
               aria-hidden="true"
               :style="privacy1b ? 'background-color:#6A40D8; border-color:#6A40D8' : 'background-color:white; border-color:#94a3b8'">
            <svg v-if="privacy1b" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" class="w-3 h-3" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
            </svg>
          </div>
          <span class="text-xs text-slate-600 text-start">
            Ho letto l'<NuxtLink class="text-primary hover:text-primary/80 font-bold" to="/privacy-policy" target="_blank">Informativa Privacy</NuxtLink>.
            Prendo atto che i miei dati saranno trattati per fornirmi i preventivi e, in caso di mia scelta di un'offerta,
            i vostri consulenti mi contatteranno e comunicheranno i dati al Fornitore Partner per finalizzare la vendita e attivare il contratto. <strong>(Obbligatorio)</strong>
          </span>
        </label>
      </div>
      <span v-if="hasError1b" id="privacy1b-error" role="alert" class="text-xs text-red-600 font-bold text-start ml-7">{{ error1b }}</span>
    </div>

    <!-- Dettaglio collassabile -->
    <div class="ml-7 flex flex-col gap-2">
      <button
        type="button"
        class="text-primary text-xs font-bold underline text-start"
        :aria-expanded="detailsOpen"
        aria-controls="privacy-details"
        @click="detailsOpen = !detailsOpen"
      >
        {{ detailsOpen ? '▲' : '▼' }} Clicca qui per i dettagli sui Canali di contatto e l'Attivazione
      </button>
      <div v-if="detailsOpen" id="privacy-details" class="text-xs leading-relaxed text-gray-600 bg-black/5 p-3 rounded-lg">
        Per illustrarti i risultati della comparazione, i nostri consulenti ti contatteranno tramite: Telefono, Email, SMS o WhatsApp.
        Se deciderai di accettare un'offerta, al fine di erogare il servizio richiesto, agiremo come intermediari caricando i tuoi dati
        direttamente sui sistemi informatici del Fornitore scelto (nei settori luce, gas o internet) per procedere all'attivazione della fornitura.
        Consulta <NuxtLink to="/lista-dei-partner" target="_blank" class="text-primary font-bold underline">qui</NuxtLink> l'elenco dei Partner.
      </div>
    </div>

    <hr class="w-full border-gray-200" />

    <!-- Consenso 3: Marketing (facoltativo) -->
    <div class="flex justify-start items-start gap-2">
      <input
        type="checkbox"
        id="privacy2-checkbox"
        :checked="privacy2"
        class="sr-only"
        @change="togglePrivacy2"
      />
      <label for="privacy2-checkbox" class="flex items-start gap-2 cursor-pointer select-none">
        <div class="w-5 h-5 min-w-5 min-h-5 shrink-0 mt-0.5 border-2 rounded-sm flex items-center justify-center transition-colors duration-150"
             aria-hidden="true"
             :style="privacy2 ? 'background-color:#6A40D8; border-color:#6A40D8' : 'background-color:white; border-color:#94a3b8'">
          <svg v-if="privacy2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" class="w-3 h-3" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
          </svg>
        </div>
        <span class="text-xs text-slate-600 text-start">
          <strong>Trattamento dati per finalità di marketing</strong>
          <span class="font-normal text-slate-500"> (facoltativo)</span>
          — Acconsento al trattamento dei miei dati personali per ricevere comunicazioni commerciali e di marketing da RisparmiaTu.com,
          tramite canali sia diretti (es. contatto telefonico, SMS, newsletter, offerte speciali) sia indiretti
          (es. analisi per migliorare la pertinenza delle comunicazioni e l'esperienza cliente).
        </span>
      </label>
    </div>

    <hr class="w-full border-gray-200" />

    <!-- Consenso 4: Comunicazione a terzi (facoltativo) -->
    <div class="flex justify-start items-start gap-2">
      <input
        type="checkbox"
        id="privacy3-checkbox"
        :checked="privacy3"
        class="sr-only"
        @change="togglePrivacy3"
      />
      <label for="privacy3-checkbox" class="flex items-start gap-2 cursor-pointer select-none">
        <div class="w-5 h-5 min-w-5 min-h-5 shrink-0 mt-0.5 border-2 rounded-sm flex items-center justify-center transition-colors duration-150"
             aria-hidden="true"
             :style="privacy3 ? 'background-color:#6A40D8; border-color:#6A40D8' : 'background-color:white; border-color:#94a3b8'">
          <svg v-if="privacy3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" class="w-3 h-3" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
          </svg>
        </div>
        <span class="text-xs text-slate-600 text-start">
          <strong>Consenso comunicazione a terzi per finalità di marketing</strong>
          <span class="font-normal text-slate-500"> (facoltativo)</span>
          — Acconsento alla comunicazione dei miei dati personali a partner commerciali selezionati di RisparmiaTu.com
          (appartenenti ai settori specificati nell'<NuxtLink class="text-primary hover:text-primary/80 font-bold" to="/privacy-policy" target="_blank">Informativa Privacy</NuxtLink>)
          per le loro finalità di marketing, attraverso canali sia diretti (es. newsletter, offerte speciali, promozioni tramite email, SMS o telefono)
          che indiretti (es. analisi di mercato, miglioramento dell'offerta e attività di branding).
        </span>
      </label>
    </div>

    <hr class="w-full border-gray-200" />

    <!-- Consenso 5: Profilazione (facoltativo) -->
    <div class="flex justify-start items-start gap-2">
      <input
        type="checkbox"
        id="privacy4-checkbox"
        :checked="privacy4"
        class="sr-only"
        @change="togglePrivacy4"
      />
      <label for="privacy4-checkbox" class="flex items-start gap-2 cursor-pointer select-none">
        <div class="w-5 h-5 min-w-5 min-h-5 shrink-0 mt-0.5 border-2 rounded-sm flex items-center justify-center transition-colors duration-150"
             aria-hidden="true"
             :style="privacy4 ? 'background-color:#6A40D8; border-color:#6A40D8' : 'background-color:white; border-color:#94a3b8'">
          <svg v-if="privacy4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" class="w-3 h-3" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="1.5,6 4.5,9.5 10.5,2.5" />
          </svg>
        </div>
        <span class="text-xs text-slate-600 text-start">
          <strong>Trattamento dati per finalità di profilazione</strong>
          <span class="font-normal text-slate-500"> (facoltativo)</span>
          — Acconsento al trattamento dei miei dati personali per finalità di profilazione, al fine di ricevere offerte commerciali
          e contenuti personalizzati da RisparmiaTu.com, basati sulle mie preferenze, abitudini di consumo e interessi
          (come descritto nell'<NuxtLink class="text-primary hover:text-primary/80 font-bold" to="/privacy-policy" target="_blank">Informativa Privacy</NuxtLink>).
        </span>
      </label>
    </div>

  </div>
</template>
